import { ReactNode } from 'react';

/**
 * The look of the pages an AI agent sends a person to: authorizing the agent
 * (ConnectAgent) and claiming what it deployed on a temporary account
 * (ClaimApps). The site's colours and type, one bordered box the size of its
 * content, few words. Results are words only, with no icon.
 */

export const SORA = 'Sora, Inter, sans-serif';
export const MONO = "'Roboto Mono', monospace";

export const Page = ({ children, low }: { children: ReactNode; low?: boolean }) => (
  <div
    className="flex min-h-screen flex-col text-[#E6EBF2]"
    style={{
      fontFamily: 'Inter, system-ui, sans-serif',
      background:
        'radial-gradient(1000px 720px at 80% 72%, #1B3356 0%, #132038 55%, #101828 100%)',
    }}
  >
    <header className="flex h-[88px] shrink-0 items-center justify-between border-b border-white/[0.06] px-5 sm:px-16">
      <span
        className="text-[30px] font-semibold tracking-[-0.01em] text-white"
        style={{ fontFamily: SORA }}
      >
        back4app
      </span>
      <a
        className="text-sm text-[#5DBB72] hover:text-[#7ED08F] hover:underline"
        href="https://www.back4app.com/docs"
        target="_blank"
        rel="noreferrer"
      >
        Help
      </a>
    </header>
    <main
      className={`flex grow flex-col items-center gap-4 px-4 pb-16 ${
        low ? 'pt-24 sm:pt-40' : 'pt-16 sm:pt-28'
      }`}
    >
      {children}
    </main>
  </div>
);

export const Card = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full max-w-[440px] flex-col gap-6 rounded-2xl border border-[#2D3C58] px-6 pb-7 pt-9 sm:px-9">
    {children}
  </div>
);

export const Title = ({ children, large }: { children: ReactNode; large?: boolean }) => (
  <h1
    className={`m-0 text-center font-semibold tracking-[-0.02em] text-white ${
      large ? 'text-[26px] leading-[1.2]' : 'text-[22px] leading-[1.25]'
    }`}
    style={{ fontFamily: SORA }}
  >
    {children}
  </h1>
);

export const Text = ({ children }: { children: ReactNode }) => (
  <p className="m-0 text-center text-[15px] leading-[1.5] text-[#AEB8C8]">{children}</p>
);

export const Fine = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col items-center gap-3 text-center text-[13px] leading-[1.5] text-[#7F8BA0]">
    {children}
  </div>
);

/** A result: words only, no icon. */
export const Result = ({ title, text, fine }: { title: string; text: ReactNode; fine?: ReactNode }) => (
  <Page low>
    <Card>
      <div className="flex flex-col items-center gap-2.5">
        <Title>{title}</Title>
        <Text>{text}</Text>
      </div>
      {fine && <Fine>{fine}</Fine>}
    </Card>
  </Page>
);
